import { Spinner } from '@sonnen/web-ui';
import { useAuth } from '@sonnen/web-utils/hooks';
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AppRoute } from '../../constants';
import { useCurrentRoute } from '../../hooks/useCurrentRoute';
import { useTranslation } from '../../i18n/i18n';
import { setRouteAfterLogin } from '../../utils/utils';
import styles from './AuthGuard.module.css';

interface AuthGuardProps {
  children: React.ReactNode;
  allowVisitor?: boolean;
}

export const AuthGuard = ({ children, allowVisitor = false }: AuthGuardProps) => {
  const { authenticated, user } = useAuth();
  const navigate = useNavigate();
  const currentRoute = useCurrentRoute();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();

  const [loading, setLoading] = useState(true);

  const utmSource = searchParams.get('utm_source');
  const isMobileSession = utmSource?.toLowerCase() === 'mobile';
  const isVisitor = allowVisitor && !user && !authenticated && !isMobileSession;

  useEffect(() => {
    // If visitor, skip auth checks
    if (isVisitor) {
      setLoading(false);
      return;
    }

    // If not authenticated, redirect to login and save current route for after login
    if (!authenticated && !user) {
      setRouteAfterLogin(currentRoute);
      navigate(AppRoute.LOGIN);
    } else if (authenticated && user) {
      // If authenticated, clear any saved route after login
      setRouteAfterLogin();
      setLoading(false);
    }
  }, [authenticated, user, isVisitor]);

  if (loading) {
    return (
      <div className={styles.spinnerWrapper}>
        <Spinner size="xl" accessibilityLabel={t('loadingUserDetails')} />
      </div>
    );
  }

  return <>{children}</>;
};
